import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CheckCircle2, ShieldCheck, Award, Wrench, Truck } from "lucide-react";
import { api } from "@/lib/api";
import TankPlaceholder from "@/components/TankPlaceholder";

const INDUSTRY_CARD_IMAGES = {
  "Water Treatment": "/sectors/water-treatment.webp",
  "Agriculture & Irrigation": "/sectors/agriculture-irrigation.webp",
  "Water & Utilities": "/sectors/water-utilities.webp",
  "Healthcare": "/sectors/healthcare.png",
  "Hotels & Hospitality": "/sectors/hotels-hospitality.webp",
  "Fire Protection": "/sectors/fire-protection.webp",
  "Infrastructure": "/sectors/infrastructure.webp",
  "Industrial": "/sectors/industrial.webp",
  "Commercial & Residential": "/sectors/commercial-residential.webp",
};

const INDUSTRY_CARD_POSITIONS = {
  "Fire Protection": "center 24%",
  "Commercial & Residential": "center 45%",
};

function PageHeader({ eyebrow, title, subtitle, image, hideContent = false, fullBanner = false, bannerAspect = "3 / 1" }) {
  return (
    <section style={fullBanner ? { aspectRatio: bannerAspect } : undefined} className={`relative ${fullBanner ? "min-h-0" : "min-h-[52vh]"} bg-pipeco-dark text-white flex items-end overflow-hidden`}>
      {image && (<><img src={image} alt="" className={`absolute inset-0 w-full h-full ${fullBanner ? "object-contain" : "object-cover opacity-45"}`}/>{!fullBanner && <div className="absolute inset-0 bg-gradient-to-t from-pipeco-dark via-black/60 to-transparent"/>}</>)}
      {!hideContent && <div className="relative container-x px-6 md:px-10 py-24 md:py-32">
        <div className="overline text-emerald-300">{eyebrow}</div>
        <h1 className="headline text-5xl md:text-7xl mt-4 max-w-4xl">{title}</h1>
        {subtitle && <p className="text-white/70 mt-5 max-w-2xl">{subtitle}</p>}
      </div>}
    </section>
  );
}

export function About() {
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow="About PIPECO GROUP" title="Engineering Water Storage Since 2010." subtitle="A global engineering group specialising in water storage manufacturing, worldwide supply, installation and long-term after-sales support." image="/tanks/shared-page-hero.webp"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-6">
            <div className="overline text-pipeco-green">Our Story</div>
            <h2 className="headline text-4xl mt-3 text-slate-900">A Decade of Engineering Water Storage.</h2>
            <p className="mt-6 text-slate-700 leading-relaxed">Since 2010, PIPECO GROUP has grown through engineering experience, water storage expertise, multiple tank technologies, international project support, quality-focused manufacturing and long-term customer relationships. We supply GRP, HDG steel, GLS/GFS, Zinc Aluminium, FRP and custom engineered water storage tanks worldwide.</p>
            <p className="mt-4 text-slate-700 leading-relaxed">Our engineers, manufacturing team and international installation crews deliver a complete package: from concept engineering through manufacturing, delivery, on-site installation, commissioning, warranty and after-sales support.</p>
          </div>
          <div className="lg:col-span-6 relative aspect-4-5 overflow-hidden">
            <img src="/tanks/about-zinc-aluminium-tank.jpeg" alt="PIPECO tank installation" className="w-full h-full object-cover"/>
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"/>
            <div className="absolute bottom-6 left-6 text-white font-mono text-[10px] tracking-widest uppercase">Real PIPECO Installation</div>
          </div>
        </div>
      </section>
      <section className="section-pad bg-pipeco-neutral">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 gap-8">
          <div className="p-10 bg-white border border-black/5">
            <div className="overline text-pipeco-green">Our Mission</div>
            <p className="mt-4 text-slate-700 leading-relaxed">To provide high-performance water storage systems through engineering precision, manufacturing excellence, international quality compliance and end-to-end project support. We strive to advance sustainable solutions, strengthen long-term partnerships and create lasting value across projects worldwide.</p>
          </div>
          <div className="p-10 bg-pipeco-dark text-white">
            <div className="overline text-emerald-300">Our Vision</div>
            <p className="mt-4 text-white/80 leading-relaxed">To become a trusted global leader in engineered water storage solutions through quality, innovation, reliability and customer-focused project support.</p>
          </div>
        </div>
      </section>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10">
          <div className="overline text-pipeco-green">Core Values</div>
          <h2 className="headline text-4xl mt-3 text-slate-900">What we stand for.</h2>
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {[[Award, "Engineering Quality"],[ShieldCheck, "Long-Term Partnership"],[Wrench, "Project Ownership"],[Truck, "Global Delivery"]].map(([Ic,t])=>(
              <div key={t} className="border-t-2 border-pipeco-green pt-6"><Ic className="text-pipeco-green" size={26}/><h3 className="font-display text-xl mt-4 text-slate-900">{t}</h3></div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function Manufacturing() {
  const steps = [
    ["Engineering","Design engineering and technical drawings tailored to each project."],
    ["Material Preparation","Careful preparation and QA of GRP, steel and coating materials."],
    ["GRP Panel Manufacturing","Hot-press moulded GRP panels manufactured to consistent tolerances."],
    ["Steel Tank Manufacturing","Cutting, forming, bolt-hole punching and coating of steel panels."],
    ["Production","Panel finishing, gasket & fastener preparation, kit assembly."],
    ["Quality Control","Full QC inspection routines applied at every production stage."],
    ["Testing","Panel and material testing under PIPECO controlled procedures."],
    ["Inspection","Independent internal inspection prior to release."],
    ["Packaging","Export-grade packaging for global shipping."],
    ["Logistics","Coordinated worldwide freight and shipping."],
    ["Worldwide Export","Delivery to international project sites across regions."],
  ];
  return (
    <div data-testid="manufacturing-page">
      <PageHeader eyebrow="Manufacturing" title="Engineering, Precision & Global Export." subtitle="From engineering to worldwide export — PIPECO's manufacturing process, step by step." image="/tanks/manufacturing-hero.webp"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {steps.map(([t,d],i) => (
            <div key={t} className="p-8 border border-black/5 card-lift bg-white">
              <div className="font-mono text-xs text-pipeco-green">STEP {String(i+1).padStart(2,"0")}</div>
              <h3 className="font-display text-2xl mt-3 text-slate-900">{t}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Installation() {
  const steps = [
    ["Site Assessment","Pre-installation site survey and coordination."],
    ["Foundation Coordination","Foundation drawings and site coordination support."],
    ["Delivery","Coordinated delivery of tank panels and accessories."],
    ["Tank Assembly","On-site assembly by trained PIPECO installation teams."],
    ["Accessories","Fitting of ladders, hatches, level sensors, valves & fittings."],
    ["Connections","Pipework connections, nozzle terminations and interfaces."],
    ["Testing","Hydrostatic and leak testing procedures."],
    ["Commissioning","System commissioning and hand-over."],
    ["Final Inspection","Final inspection and documentation."],
    ["Warranty Support","Warranty registration and long-term support."],
    ["After-Sales Service","Ongoing after-sales, service and spare parts."],
  ];
  return (
    <div data-testid="installation-page">
      <PageHeader eyebrow="Installation" title="Professional Site Installation & Commissioning." image="https://images.unsplash.com/photo-1518623380242-d992d3c57b48?w=2000&q=85"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {steps.map(([t,d],i) => (
            <div key={t} className="p-8 bg-pipeco-neutral card-lift">
              <div className="font-mono text-xs text-pipeco-green">STEP {String(i+1).padStart(2,"0")}</div>
              <h3 className="font-display text-2xl mt-3 text-slate-900">{t}</h3>
              <p className="text-sm text-slate-600 mt-2 leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Industries() {
  const [industries, setIndustries] = useState([]);
  useEffect(()=>{ api.get("/industries").then(r=>setIndustries(r.data)); },[]);
  return (
    <div data-testid="industries-page">
      <PageHeader eyebrow="Industries" title="Purpose-Built for Every Sector." image="/tanks/shared-page-hero.webp"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {industries.map(ind => (
            <div key={ind.id} className="relative aspect-4-5 overflow-hidden bg-pipeco-dark group">
              {INDUSTRY_CARD_IMAGES[ind.name] || ind.image ? (
                <>
                  <img src={INDUSTRY_CARD_IMAGES[ind.name] || ind.image} alt={ind.name} className="absolute inset-0 w-full h-full object-cover opacity-55 group-hover:opacity-40 group-hover:scale-105 transition-all duration-500" style={{objectPosition: INDUSTRY_CARD_POSITIONS[ind.name] || "center"}}/>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/85 to-transparent"/>
                  <div className="absolute inset-0 p-8 flex flex-col justify-end text-white">
                    <h3 className="headline text-3xl">{ind.name}</h3>
                    <p className="text-sm text-white/70 mt-2">{ind.description}</p>
                    <Link to="/products" className="mt-4 overline text-emerald-300 hover-line">View Solutions →</Link>
                  </div>
                </>
              ) : (
                <>
                  <TankPlaceholder label={ind.name} subtitle="Industry" tone="dark"/>
                  <div className="absolute bottom-8 left-8 right-8 z-10">
                    <p className="text-xs text-white/70 mt-1">{ind.description}</p>
                    <Link to="/products" className="mt-3 inline-block overline text-emerald-300 hover-line">View Solutions →</Link>
                  </div>
                </>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Quality() {
  const items = [["Quality Control","Full internal QC across every production stage."],["Material Inspection","Incoming material inspection and traceability."],["Manufacturing Standards","Manufacturing controlled under PIPECO standards."],["Testing","In-house and on-site tank testing routines."],["Traceability","Full material and production traceability."],["Inspection","Independent internal inspection."],["Engineering Quality","Design engineering reviews per project."],["Project Compliance","International project compliance support."]];
  const [certifications, setCertifications] = useState([]);
  useEffect(()=>{ api.get("/documents").then(r=>setCertifications(r.data.filter(d=>d.published && d.category === "Certifications"))); },[]);
  return (
    <div data-testid="quality-page">
      <PageHeader eyebrow="Quality & Certifications" title="Compliance Engineered In." subtitle="Certifications, standards and quality control procedures. Real certification documents are uploaded via the admin panel." image="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=2000&q=85"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-4 gap-4">
          {items.map(([t,d])=>(
            <div key={t} className="p-8 bg-pipeco-neutral"><CheckCircle2 className="text-pipeco-green" size={22}/><h3 className="font-display text-xl mt-3 text-slate-900">{t}</h3><p className="text-sm text-slate-600 mt-2">{d}</p></div>
          ))}
        </div>
        <div className="container-x px-6 md:px-10 mt-14">
          <div className="p-8 bg-pipeco-dark text-white">
            <div className="overline text-emerald-300">Certifications</div>
            {certifications.length ? <div className="grid md:grid-cols-2 gap-3 mt-5">{certifications.map(doc=><div key={doc.id} className="border border-white/15 p-4 flex items-center justify-between gap-4"><div><div className="text-sm font-medium">{doc.title}</div><div className="text-xs text-white/60 mt-1">{doc.category}</div></div>{doc.pdf_url&&<a data-testid={`quality-certificate-${doc.id}`} href={doc.pdf_url} target="_blank" rel="noreferrer" className="overline text-emerald-300 whitespace-nowrap">Open</a>}</div>)}</div> : <p className="text-white/70 mt-3">Certification documents will be available here once published.</p>}
          </div>
        </div>
      </section>
    </div>
  );
}

export function Warranty() {
  const [products, setProducts] = useState([]);
  useEffect(()=>{ api.get("/products").then(r=>setProducts(r.data)); },[]);
  return (
    <div data-testid="warranty-page">
      <PageHeader eyebrow="Warranty & After-Sales" title="Support That Outlasts Handover." subtitle="Warranty terms are managed per product via the admin panel. Contact us for the current warranty document for your project." image="https://images.unsplash.com/photo-1519494026892-80bbd2d6fd0d?w=2000&q=85"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 gap-6">
          {products.map(p => (
            <div key={p.id} className="p-8 border border-black/5 bg-white">
              <div className="overline text-pipeco-green">{p.name}</div>
              <h3 className="font-display text-xl mt-2 text-slate-900">Warranty Details</h3>
              <p className="text-sm text-slate-600 mt-2">{p.warranty || "Warranty details available on request per project scope."}</p>
              <Link to={`/products/${p.slug}`} className="mt-4 inline-block overline text-pipeco-green hover-line">Product page →</Link>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}

export function Resources() {
  const [docs, setDocs] = useState([]);
  const [cat, setCat] = useState("All");
  useEffect(()=>{ api.get("/documents").then(r=>setDocs(r.data)); },[]);
  const cats = ["All", ...Array.from(new Set(docs.map(d=>d.category)))];
  const filtered = cat==="All"?docs:docs.filter(d=>d.category===cat);
  return (
    <div data-testid="resources-page">
      <PageHeader eyebrow="Resources" title="Download Center." subtitle="Company profile, product catalogues, technical datasheets, installation manuals, warranty documents and certifications." image="https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?w=2000&q=85"/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10">
          <div className="flex flex-wrap gap-2 mb-8">
            {cats.map(c => <button key={c} onClick={()=>setCat(c)} className={`px-4 py-2 text-xs uppercase tracking-widest border ${cat===c?"bg-pipeco-green text-white border-pipeco-green":"border-black/10 text-slate-700 hover:border-pipeco-green"}`}>{c}</button>)}
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filtered.map(d => (
              <div key={d.id} className="bg-white border border-black/5 card-lift">
                <div className="aspect-4-5 overflow-hidden bg-pipeco-green">
                  {d.cover_image
                    ? <img src={d.cover_image} alt={d.title} className="w-full h-full object-cover"/>
                    : <TankPlaceholder label={d.title} subtitle={d.category} tone="dark"/>}
                </div>
                <div className="p-6">
                  <div className="overline text-pipeco-green">{d.category}</div>
                  <h3 className="font-display text-lg mt-2 text-slate-900">{d.title}</h3>
                  <p className="text-xs text-slate-500 mt-1">{d.description}</p>
                  <div className="flex gap-2 mt-4">
                    <a href={d.pdf_url || "#"} target="_blank" rel="noreferrer" className="btn-outline-dark flex-1 justify-center text-[10px]">View</a>
                    <a href={d.pdf_url || "#"} target="_blank" rel="noreferrer" className="btn-primary flex-1 justify-center text-[10px]">Download</a>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}

export function News() {
  const [news, setNews] = useState([]);
  useEffect(()=>{ api.get("/news").then(r=>setNews(r.data)); },[]);
  return (
    <div data-testid="news-page">
      <PageHeader eyebrow="Latest News" title="Announcements, Updates & Insights." image="/tanks/news-hero-new-banner.webp" hideContent fullBanner/>
      <section className="section-pad bg-white">
        <div className="container-x px-6 md:px-10 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {news.map(n => (
            <div key={n.id} className="bg-white card-lift border border-black/5">
              <div className="aspect-16-9 overflow-hidden bg-pipeco-green">
                {n.featured_image
                  ? <img src={n.featured_image} alt={n.title} className="w-full h-full object-cover"/>
                  : <TankPlaceholder label={n.category} subtitle="PIPECO News" tone="dark"/>}
              </div>
              <div className="p-6">
                <div className="overline text-pipeco-green">{n.category}</div>
                <h3 className="font-display text-xl mt-2 text-slate-900">{n.title}</h3>
                <p className="text-sm text-slate-600 mt-2 line-clamp-3">{n.content}</p>
                <div className="text-xs text-slate-400 mt-3">{new Date(n.date).toLocaleDateString()}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
